import { ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule, ModalController } from '@ionic/angular';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { ConfirmPage } from 'src/pages/confirm/confirm.component';

import { Util } from 'src/shared/util/util';
import { readSecondaryPageContext } from 'src/shared/util/secondary-page-context';

import { AddressesQueriesService } from 'src/shared/services/addresses-queries.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { FinanceTypesService } from 'src/shared/services/finance-types.service';
import {
  FinancialTransactionRule,
  FinancialTransactionRulesService,
  RuleCondition,
} from 'src/shared/services/financial-transaction-rules.service';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

export interface RuleView {
  id: number;
  rule: FinancialTransactionRule;
  conditionsLabel: string;
  actionLabel: string;
  createdByLabel: string;
}

const OP_LABELS: Record<string, string> = {
  equals: 'is exactly',
  contains: 'contains',
  matches: 'matches pattern',
};

const RULES_DESCRIPTION = 'Rules decide what happens to new bank transactions as they arrive. TIDY checks them top to bottom and the first match wins. Anything no rule matches goes to TIDY AI, which may add its own rule here. Drag a rule to change its order. Rules apply to new transactions only; nothing already categorized changes.';

/**
 * The customer's classification rules in the order the classifier walks them.
 * Drag to reorder; first match wins. Global TIDY rules are not returned by the API.
 */
@Component({
  templateUrl: 'transaction-rules.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, IonicModule, TidyUiComponentsModule, SharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TransactionRulesPage implements OnInit, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly modalCtrl = inject(ModalController);
  private readonly util = inject(Util);
  private readonly demoBlock = inject(DemoBlockService);
  private readonly addressesQueries = inject(AddressesQueriesService);
  private readonly financeTypes = inject(FinanceTypesService);
  private readonly rulesService = inject(FinancialTransactionRulesService);

  readonly infoIconSrc = 'assets/svg/information-circle-outline.svg';
  readonly rulesDescription = RULES_DESCRIPTION;

  isRightSideContent = true;
  loaded = false;
  errorMessage = '';
  reordering = false;
  ruleViews: RuleView[] = [];

  readonly boundNewRule = () => this.newRule();

  private typeLabels = new Map<string, string>();
  private addressLabels = new Map<number, string>();

  async ngOnInit() {
    const { isRightSideContent } = await readSecondaryPageContext(this.storage);
    this.isRightSideContent = isRightSideContent;
    // The "what is a rule" copy sits behind an info icon right after the title:
    // a hover tooltip in the desktop dialog header, a tap on the mobile nav icon.
    this.rightSidePanelService.setDialogPageTitle('Transaction Rules', RULES_DESCRIPTION);
    await this.loadPage();
  }

  ngOnDestroy() {
    if (this.isRightSideContent) {
      this.rightSidePanelService.pageTitleTooltip$.next(null);
    }
  }

  showInfo(): void {
    this.util.showAlert('Transaction Rules', RULES_DESCRIPTION, true);
  }

  ionViewWillEnter() {
    if (this.loaded) {
      this.loadPage();
    }
  }

  async loadPage(): Promise<void> {
    this.errorMessage = '';
    try {
      const [rules, types, addresses] = await Promise.all([
        this.rulesService.getRules(),
        this.financeTypes.getFinanceTypes().catch(() => []),
        this.addressesQueries.getAddressesOnce().catch(() => []),
      ]);
      this.typeLabels = new Map(types.map((type): [string, string] => [type.key, type.label]));
      this.addressLabels = new Map(addresses.map((address): [number, string] => [address.id, address['address_name'] || address.address1 || `Property #${address.id}`]));
      this.ruleViews = rules.map(rule => this.toView(rule));
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load rules';
      this.ruleViews = [];
    }
    this.loaded = true;
    this.cdr.markForCheck();
  }

  private toView(rule: FinancialTransactionRule): RuleView {
    return {
      id: rule.id,
      rule,
      conditionsLabel: this.describeConditions(rule.conditions),
      actionLabel: this.describeApplies(rule),
      createdByLabel: rule.created_by === 'llm' ? 'Suggested by TIDY AI' : rule.created_by === 'admin' ? 'Added by TIDY' : 'Created by you',
    };
  }

  private describeConditions(conditions: RuleCondition[]): string {
    return (conditions || []).map(condition => {
      if (condition.type === 'transaction_type') {
        return `type is ${condition.value}`;
      }
      return `description ${OP_LABELS[condition.op] || condition.op} "${condition.value}"`;
    }).join(' and ');
  }

  private describeApplies(rule: FinancialTransactionRule): string {
    const applies = rule.applies;
    if (applies.classification === 'skip') { return 'Skip, not for the P&L'; }
    if (applies.classification === 'unknown') { return 'Leave for review'; }
    const type = applies.monthly_finance_type_key ? this.typeLabels.get(applies.monthly_finance_type_key) || applies.monthly_finance_type_key : 'uncategorized';
    const address = applies.address_id ? this.addressLabels.get(applies.address_id) || `Property #${applies.address_id}` : null;
    return address ? `Add to P&L as ${type} · ${address}` : `Add to P&L as ${type}`;
  }

  async onReordered(list: RuleView[]): Promise<void> {
    if (this.demoBlock.block()) {
      await this.loadPage();
      return;
    }
    this.reordering = true;
    this.cdr.markForCheck();
    try {
      await this.rulesService.reorderRules(list.map(view => view.id));
    } catch (err) {
      this.util.showError(err?.error?.message || err?.message || 'Unable to reorder rules');
      await this.loadPage();
    } finally {
      this.reordering = false;
      this.cdr.markForCheck();
    }
  }

  newRule(): void {
    this.rightSidePanelService.navigateTo('transaction-rule-form', { rule: null, sample: null, suggestedApplies: null });
  }

  editRule(view: RuleView): void {
    this.rightSidePanelService.navigateTo('transaction-rule-form', { rule: view.rule, sample: null, suggestedApplies: null });
  }

  async confirmDelete(view: RuleView): Promise<void> {
    if (this.demoBlock.block()) { return; }
    const modal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: {
        title: 'Delete Rule',
        body: 'Future bank transactions will no longer match this rule. Transactions already categorized do not change.',
        backText: 'Cancel',
        confirmText: 'Delete',
        confirmAction: async () => {
          try {
            await this.rulesService.deleteRule(view.id);
            this.ruleViews = this.ruleViews.filter(v => v.id !== view.id);
            this.util.showSuccess('Rule deleted');
          } catch (err) {
            this.util.showError(err?.error?.message || err?.message || 'Unable to delete the rule');
          }
          modal.dismiss();
          this.cdr.markForCheck();
        },
        confirmButtonClass: 'primary',
      },
      animated: false,
      cssClass: 'confirm-modal',
    });
    modal.present();
  }

  goBack(): void {
    if (this.isRightSideContent) {
      if (this.rightSidePanelService.historyContext$.getValue().length > 1) {
        this.rightSidePanelService.goBack();
      } else {
        this.rightSidePanelService.closeRightSidePanel();
      }
    } else {
      this.navCtrl.back();
    }
  }
}
